import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CheckoutFlow from '../components/CheckoutFlow';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { ShoppingCart, Trash, CheckCircle } from 'phosphor-react';

// Cart Item Component - Updated to match mobile cart modal styling
function CartItem({ item, onUpdateQuantity, onHandleQuantityUpdate, onRemove, t, showToast, toastMessage }) {
  return (
    <div className="flex items-center space-x-4 p-4 border-b border-gray-100 last:border-b-0">
      <div className="w-16 h-16 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden flex-shrink-0">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className="w-full h-full bg-gradient-to-br from-ambre-100 to-ambre-200 flex items-center justify-center">
          <span className="text-xs font-medium text-ambre-600">Card</span>
        </div>
      </div>
      
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-gray-900 truncate">{item.name}</h4>
        <p className="text-xs text-gray-500">{t('product.quantity')}: {item.quantity}</p>
        <div className="text-sm font-medium text-ambre-600">
          €{(item.price * item.quantity).toFixed(2)}
        </div>
      </div>
      
      <div className="flex items-center space-x-2">
        <button
          onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
          className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition"
        >
          <span className="text-gray-600 text-sm">-</span>
        </button>
        <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
        <button
          onClick={() => onHandleQuantityUpdate(item.id, item.quantity + 1)}
          className={`w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition ${
            item.stock_quantity !== undefined && item.quantity >= item.stock_quantity 
              ? 'opacity-50 cursor-not-allowed' 
              : ''
          }`}
          title={item.stock_quantity !== undefined && item.quantity >= item.stock_quantity ? `Maximum ${item.stock_quantity} beschikbaar` : ''}
        >
          <span className="text-gray-600 text-sm">+</span>
        </button>
        <button
          onClick={() => onRemove(item.id)}
          className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center hover:bg-red-200 transition ml-2"
        >
          <Trash size={16} className="text-red-600" />
        </button>
      </div>
      
      {showToast && (
        <div className="absolute top-0 left-0 right-0 bg-red-100 text-red-800 text-xs p-2 text-center">
          {toastMessage}
        </div>
      )}
    </div>
  );
}

// Discount Item Component - Updated to match mobile cart modal styling
function DiscountItem({ discount, t }) {
  return (
    <div className="flex items-center space-x-4 p-4 bg-green-50 border-b border-green-100 last:border-b-0">
      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
        <span className="text-green-600 text-sm font-bold">%</span>
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-green-800 truncate">{discount.name}</h4>
        <p className="text-xs text-green-600">Korting toegepast</p>
      </div>
      <div className="text-sm font-medium text-green-600">
        €{discount.price.toFixed(2)}
      </div>
    </div>
  );
}

function Cart() {
  const { t } = useTranslation();
  const { items, removeFromCart, updateQuantity, updateQuantityWithStockCheck, updateCartItemStock, getCartTotal, getCartSubtotal, getCartShipping, getDiscountItems, getCartItemsCount, getCartDiscount } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastItemId, setToastItemId] = useState(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle quantity update with stock validation and user feedback
  const handleQuantityUpdate = async (productId, newQuantity) => {
    const item = items.find(item => item.id === productId);
    if (!item) return;

    // Check stock limit
    if (item.stock_quantity !== undefined && item.stock_quantity > 0) {
      if (newQuantity > item.stock_quantity) {
        setToastMessage(`Maximum ${item.stock_quantity} beschikbaar`);
        setToastItemId(productId);
        setShowToast(true);
        
        // Hide toast after 5 seconds
        setTimeout(() => {
          setShowToast(false);
          setToastItemId(null);
        }, 5000);
        return;
      }
    }

    updateQuantity(productId, newQuantity);
  };

  const handleProceedToCheckout = () => {
    if (items.length === 0) {
      setError(t('cart.empty'));
      return;
    }
    setError('');
    setIsCheckingOut(true);
    // Scroll to top when transitioning to checkout
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCheckoutCancel = () => {
    setIsCheckingOut(false);
  };

  const handleCheckoutComplete = (orderData) => {
    console.log('Checkout complete! Navigating to home page.', orderData);
    navigate('/');
  };

  if (isCheckingOut) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="py-12 px-4 sm:px-6 lg:px-8">
          <CheckoutFlow
            onComplete={handleCheckoutComplete}
            onCancel={handleCheckoutCancel}
          />
        </div>
      </div>
    );
  }

  // Show cart screen
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      <div className="flex-1 max-w-6xl mx-auto px-4 py-12 mb-16 w-full">
        <h1 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
          {/* <ShoppingCart size={32} className="mr-3 text-ambre-600" /> */}
          {t('cart.title')}
        </h1>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {items.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <ShoppingCart size={64} className="mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">{t('cart.empty')}</h2>
            <p className="text-gray-500 mb-6">{t('cart.emptySubtitle')}</p>
            <button
              onClick={() => navigate('/shop')}
              className="bg-ambre-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-ambre-700 transition"
            >
              {t('cart.continueShopping')}
            </button>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Cart Items */}
            <div className="flex-1">
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">{t('cart.title')} ({getCartItemsCount()} {t('common.items')})</h2>
                </div>
                <div className="bg-white">
                  {items.map((item) => (
                    <CartItem
                      key={item.id}
                      item={item}
                      onUpdateQuantity={updateQuantity}
                      onHandleQuantityUpdate={handleQuantityUpdate}
                      onRemove={removeFromCart}
                      t={t}
                      showToast={showToast && toastItemId === item.id}
                      toastMessage={toastMessage}
                    />
                  ))}
                  
                  {/* Discount Items */}
                  {getDiscountItems().map((discount) => (
                    <DiscountItem key={discount.id} discount={discount} t={t} />
                  ))}
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:w-[28rem] w-full">
              <div className="bg-white rounded-xl shadow-sm p-6 sticky top-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('checkout.orderSummary')}</h2>
                <div className="space-y-3">
                  <div className="flex justify-between text-gray-600">
                    <span className="flex-1">{t('cart.subtotal')} ({getCartItemsCount()} {t('common.items')})</span>
                    <span className="w-20 text-right">€{getCartSubtotal().toFixed(2)}</span>
                  </div>
                  {getDiscountItems().length > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span className="flex-1">Korting</span>
                      <span className="w-20 text-right">-€{getCartDiscount().toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-600">
                    <span className="flex-1">{t('cart.shipping')}</span>
                    <span className="w-20 text-right">{getCartShipping() === 0 ? t('cart.free') : `€${getCartShipping().toFixed(2)}`}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex justify-between text-lg font-semibold text-gray-900">
                      <span className="flex-1">{t('cart.total')}</span>
                      <span className="w-20 text-right">€{getCartTotal().toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleProceedToCheckout}
                  disabled={items.length === 0}
                  className="w-full mt-6 bg-ambre-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-ambre-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  <CheckCircle size={20} className="mr-2" />
                  {t('cart.checkout')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default Cart; 